import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#1a1a1a] text-gray-300">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Par Seeker
        </Link>
        
        <h1 className="text-3xl font-bold text-white mb-2">Terms of Service</h1>
        <p className="text-gray-500 mb-8">Last Updated: June 15, 2026</p>
        
        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Par Seeker ("the Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
            <p>
              Par Seeker is a golf score tracking application that allows users to track scores, calculate handicaps, 
              organize games with friends, and manage settlements for friendly wagers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. User Accounts</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all 
              activities that occur under your account. You must provide accurate and complete information when 
              creating an account.
            </p>
          </section>

          <section id="sms-terms">
            <h2 className="text-xl font-semibold text-white mb-3">4. SMS/Text Message Terms</h2>
            <p className="mb-3">
              By providing your mobile phone number and checking the SMS consent checkbox, you agree to receive 
              automated text messages from Par Seeker. These messages may include:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
              <li>Account verification codes</li>
              <li>Game invitations from other players</li>
              <li>Score notifications and results</li>
              <li>Settlement reminders and confirmations</li>
              <li>Important account and security alerts</li>
            </ul>
            <p className="mb-3">
              <strong className="text-white">Message Frequency:</strong> Message frequency varies based on your 
              activity and game participation. You may receive multiple messages per day during active game sessions.
            </p>
            <p className="mb-3">
              <strong className="text-white">Message and Data Rates:</strong> Message and data rates may apply 
              depending on your mobile carrier plan.
            </p>
            <p className="mb-3">
              <strong className="text-white">Opt-Out:</strong> You can opt out of receiving SMS messages at any time 
              by replying <span className="text-green-500 font-mono">STOP</span> to any message. After opting out, 
              you will receive a confirmation message and no further messages will be sent.
            </p>
            <p className="mb-3">
              <strong className="text-white">Help:</strong> For help with SMS messages, reply <span className="text-green-500 font-mono">HELP</span> to 
              any message or contact us at support@parseeker.com.
            </p>
            <p>
              <strong className="text-white">Consent Not Required:</strong> Consent to receive SMS messages is not 
              required to use Par Seeker. You may skip phone verification, though some features may be limited.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. User Conduct</h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Submit false or misleading scores</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Attempt to gain unauthorized access to other accounts</li>
              <li>Use automated systems to access the Service without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Settlements and Payments</h2>
            <p>
              Par Seeker facilitates tracking of friendly wagers between players. Par Seeker does not process 
              payments directly. All settlements are between individual users. Par Seeker is not responsible 
              for any disputes regarding payments between users.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Intellectual Property</h2>
            <p>
              All content, features, and functionality of the Service are owned by Par Seeker and are protected 
              by intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Disclaimer of Warranties</h2>
            <p>
              The Service is provided "as is" without warranties of any kind, either express or implied. 
              We do not guarantee the accuracy of handicap calculations or course information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Limitation of Liability</h2>
            <p>
              Par Seeker shall not be liable for any indirect, incidental, special, consequential, or punitive 
              damages resulting from your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Continued use of the Service after 
              changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Contact</h2>
            <p>
              For questions about these Terms, contact us at:{' '}
              <a href="mailto:support@parseeker.com" className="text-green-500 hover:underline">
                support@parseeker.com
              </a>
            </p>
          </section>
        </div>
        
        <div className="mt-12 pt-6 border-t border-gray-700 text-center text-gray-500 text-xs">
          <p>&copy; {new Date().getFullYear()} Par Seeker. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
